import { render } from "lit";
import { initLightboxes } from "@/lightbox";
import { CopyButton } from "@/components/copy_button";
import { I18n } from "@/i18n/i18n";
import type { QuestionBase } from "@/components/composite/question_base";
import { focusHeading, watchHeadings } from "@/description_headings";
import type { FrameMessage, ParentMessage } from "@/iframe_messages";

// The VS Code extension shows descriptions in a webview without window.parent and window.top, and
// iframe-resizer posts to both as soon as it is evaluated.
if (window.parent && window.top && window.parent !== window) {
    void import("@iframe-resizer/child"); // sets window.parentIFrame
}

function sendToParent(message: FrameMessage): void {
    // Parent is not available in vscode. The lightbox will not work there.
    window.parentIFrame?.sendMessage(message);
}

// Unlike sendToParent, works before iframe-resizer has connected.
function postToParent(message: FrameMessage): void {
    window.parent?.postMessage(message, "*");
}

/**
 * Marks a paragraph that holds only images, so the images can be as wide as the frame instead of
 * stopping at the reading measure of the text. The paragraph keeps its own attributes.
 */
function markImageParagraphs(): void {
    document.querySelectorAll<HTMLParagraphElement>(".description-content p").forEach((paragraph) => {
        const nodes = Array.from(paragraph.childNodes);
        const isImage = (node: Node): boolean => node instanceof HTMLImageElement;
        const isBlank = (node: Node): boolean => node.nodeType === Node.TEXT_NODE && !node.textContent.trim();
        if (!nodes.some(isImage) || !nodes.every((node) => isImage(node) || isBlank(node))) return;

        paragraph.classList.add("description-images");
    });
}

// The width a block can take inside this element.
function contentWidth(element: HTMLElement): number {
    const style = getComputedStyle(element);
    return element.clientWidth - (parseFloat(style.paddingLeft) || 0) - (parseFloat(style.paddingRight) || 0);
}

/**
 * Puts a table in a scroll wrapper while it is wider than the frame, so it scrolls on its own
 * instead of widening the page, and takes it out again when it fits. A table that fits keeps its
 * place, its float and the background around it. Keyboard users can focus the wrapper to scroll it.
 */
function fitTable(table: HTMLTableElement): void {
    const parent = table.parentElement;
    const wrapper = parent.classList.contains("table-scroll-wrapper") ? parent : null;
    const wide = table.offsetWidth > contentWidth(wrapper?.parentElement ?? parent);
    if (wide && !wrapper) {
        const region = document.createElement("div");
        region.className = "table-scroll-wrapper";
        region.tabIndex = 0;
        region.setAttribute("role", "region");
        region.setAttribute("aria-label", table.caption?.textContent.trim() || I18n.t("js.scrollable-table"));
        table.replaceWith(region);
        region.append(table);
    } else if (!wide && wrapper) {
        wrapper.replaceWith(table);
    }
}

function wrapWideTables(): void {
    const tables = Array.from(document.querySelectorAll<HTMLTableElement>(".description-content table")).filter(
        (table) => !table.closest(".dodona-centered-group") && getComputedStyle(table).float === "none",
    );
    const fit = (): void => tables.forEach(fitTable);
    fit();
    window.addEventListener("load", fit);
    window.addEventListener("resize", fit);
}

function initCodeFragments(): void {
    document.querySelectorAll<HTMLElement>("pre code").forEach((codeElement) => {
        const wrapper = codeElement.parentElement;
        wrapper.classList.add("code-wrapper");
        const copyButton = new CopyButton();
        copyButton.target = codeElement;

        render(copyButton, wrapper, { renderBefore: codeElement });
    });
}

/** Forwards the answers of the questions of a composite exercise to the hand-in on the page. */
function initCompositeCommunication(): void {
    document.querySelectorAll<QuestionBase>(".composite-question").forEach((el) => {
        postToParent({ type: "dodona-composite-register", id: el.id });
        el.addEventListener("update", () =>
            postToParent({ type: "dodona-composite-answer", id: el.id, value: el.value }),
        );
    });
}

/**
 * Answers what the page asks once iframe-resizer has connected: the headings when the page lists
 * them, and focus on a heading.
 */
function answerParent(): void {
    let headingsWatched = false;
    window.iFrameResizer = {
        onMessage(message: ParentMessage) {
            if (message.type === "send-headings" && !headingsWatched) {
                headingsWatched = true;
                watchHeadings((headings, intro) => sendToParent({ type: "headings", headings, intro }));
            } else if (message.type === "focus-heading") {
                focusHeading(message.id);
            }
        },
    };
}

/**
 * iframe-resizer 5 ignores a size change that comes while a size it sent waits for the next
 * animation frame, and does not measure again afterwards. In Safari an image without a size that
 * loaded just after iframe-resizer started left the frame too short. Asks for a measurement again
 * in a new animation frame each time the description changes height or an image in it loads, so it
 * runs after the frame iframe-resizer waits for, when iframe-resizer takes it. iframe-resizer 6
 * measures such a change in its next animation frame itself, so this can probably be removed once
 * we update to version 6.
 */
function remeasureAfterResize(): void {
    let frame: number | null = null;
    const remeasure = (): void => {
        if (frame !== null) cancelAnimationFrame(frame);
        frame = requestAnimationFrame(() => {
            frame = null;
            window.parentIFrame?.resize();
        });
    };
    new ResizeObserver(remeasure).observe(document.body);
    // The load event of an image does not bubble, so it is caught on its way down.
    document.addEventListener(
        "load",
        (event) => {
            if (event.target instanceof HTMLImageElement) remeasure();
        },
        true,
    );
}

/**
 * The page ignores a size iframe-resizer 5 sends while it is hidden, as in a background tab, but
 * iframe-resizer counts it as sent and does not send it again when the page shows, so an image that
 * loaded in the background left the frame too short. A reset sends the size again. iframe-resizer
 * 6 sends it again itself, so this can probably be removed once we update to version 6.
 */
function resendSizeWhenShown(): void {
    document.addEventListener("visibilitychange", () => {
        if (!document.hidden) window.parentIFrame?.reset();
    });
}

export function initDescription(): void {
    initLightboxes(".activity-description img, a.dodona-lightbox", (content) =>
        sendToParent({ type: "lightbox", content }),
    );
    markImageParagraphs();
    wrapWideTables();
    initCodeFragments();
    initCompositeCommunication();
    answerParent();
    remeasureAfterResize();
    resendSizeWhenShown();
}
